import type { Tab } from "../navigation.ts";

export type FleetGuideTryPrefill = { kind: "chat-draft"; text: string };

export interface FleetGuideTry {
  id: string;
  tab: Tab;
  label: string;
  banner: string;
  checklist: string[];
  prefill?: FleetGuideTryPrefill;
}

/** Stable id for the default try on a tab: `example-<tab>-1`. */
export function fleetTryIdForTab(tab: Tab): string {
  return `example-${tab}-1`;
}

const TRIES: FleetGuideTry[] = [
  {
    id: "example-overview-1",
    tab: "overview",
    label: "Connect to your gateway",
    banner: "Example: connect Chat to a running Jaisiu gateway.",
    checklist: [
      "Set Gateway URL (e.g. ws://127.0.0.1:18789).",
      "Paste your gateway token and click Connect.",
      "Wait until Jaisiu shows Online.",
    ],
  },
  {
    id: "example-agents-1",
    tab: "agents",
    label: "Point main agent at a demo workspace",
    banner: "Example: aim the main agent at a folder for the todo CLI demo.",
    checklist: [
      "Select agent main (or your default agent).",
      "Set workspace to ~/projects/todo-cli-demo (create the folder first if needed).",
      "Save, then open Files to confirm the path lists.",
    ],
  },
  {
    id: "example-skills-1",
    tab: "skills",
    label: "Enable coding skills",
    banner: "Example: turn on skills the agent needs to edit files and run commands.",
    checklist: [
      "Enable coding / terminal skills your policy allows.",
      "Add API keys only in the secure fields on this page.",
    ],
  },
  {
    id: "example-chat-1",
    tab: "chat",
    label: "Multi-agent todo CLI prompt",
    banner:
      "Example: main-session prompt (not UI auto-spawn). The main agent delegates via sessions_spawn.",
    checklist: [
      "Session must be main (e.g. agent:main:main) — not a subagent thread.",
      "Send the draft below; the main agent orchestrates subagents via tools.",
      "Expand subagent blocks in the transcript to watch parallel work.",
    ],
    prefill: {
      kind: "chat-draft",
      text: `@all Build a minimal Node.js todo CLI in this workspace.

- One subagent: add package.json and src/ with add/list commands.
- Another subagent: add a smoke test and run npm test.

Report files created and test results when finished.`,
    },
  },
  {
    id: "example-workforce-1",
    tab: "workforce",
    label: "Review delegation routing",
    banner: "Example: inspect workforce routing before re-running the chat demo.",
    checklist: [
      "Open routing / delegation rules exposed by your gateway.",
      "Note which agent or skill handles implementation vs review.",
      "Return to Chat and re-send example-chat-1 to validate routing.",
    ],
  },
  {
    id: "example-sessions-1",
    tab: "sessions",
    label: "Find sessions from the demo run",
    banner: "Example: locate session keys produced by the multi-agent chat run.",
    checklist: [
      "Find agent:main and any subagent session rows.",
      "Open Chat from a row to continue that session.",
      "Optional: note keys for a Cron job on the Cron tab.",
    ],
  },
];

const TRY_BY_ID = new Map<string, FleetGuideTry>(TRIES.map((t) => [t.id, t]));

export function getFleetGuideTry(id: string): FleetGuideTry | undefined {
  return TRY_BY_ID.get(id);
}

export function listFleetGuideTryIds(): string[] {
  return TRIES.map((t) => t.id);
}
